import { useState } from "react";
import { useFetch, apiPost, timeAgo } from "@/lib/shieldx";
import { PageHeader, Loader, Panel } from "@/components/shieldx/Primitives";
import { StatusBadge, RiskBadge, DemoTag } from "@/components/shieldx/Badges";
import { KeyRound, Check, X, Clock3, ShieldQuestion, Ban } from "lucide-react";
import { toast } from "sonner";

const DECISIONS = [
  { key: "APPROVE", label: "Approve", icon: Check, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" },
  { key: "APPROVE_ONCE", label: "Approve Once", icon: Check, cls: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/15" },
  { key: "APPROVE_TEMPORARY", label: "Approve Temporarily", icon: Clock3, cls: "border-purple-500/40 bg-purple-500/10 text-purple-300 hover:bg-purple-500/20" },
  { key: "REQUEST_VERIFICATION", label: "Request Verification", icon: ShieldQuestion, cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20" },
  { key: "DENY", label: "Deny", icon: X, cls: "border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20" },
  { key: "REJECT_PERMANENT", label: "Reject Permanently", icon: Ban, cls: "border-red-600/40 bg-red-600/10 text-red-300 hover:bg-red-600/20" },
];

export default function Authorization() {
  const { data, loading, reload } = useFetch("/authorization-requests");
  const [busy, setBusy] = useState(null);
  if (loading || !data) return <Loader label="Loading authorization center" />;

  const pending = data.filter((r) => r.status === "PENDING");
  const resolved = data.filter((r) => r.status !== "PENDING");

  const decide = async (req, decision) => {
    setBusy(req.id + decision);
    try { await apiPost(`/authorization-requests/${req.id}/decision`, { decision }); toast.success(`${req.request_id} — ${decision.replace(/_/g, " ").toLowerCase()}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader icon={KeyRound} title="Authorization Center"
        subtitle="Protected actions from autonomous and automated sources require authorization. Human authority is the final boundary."
        actions={<DemoTag />} />

      <div className="mb-3 flex items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Pending Requests</span>
        <span className="rounded-full bg-amber-500/15 px-2 py-0.5 font-mono text-[11px] text-amber-300">{pending.length}</span>
      </div>

      {pending.length === 0 && <p className="rounded-xl border border-dashed border-slate-800 py-10 text-center text-sm text-slate-500">No pending authorization requests.</p>}

      <div className="space-y-4" data-testid="pending-requests">
        {pending.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-900/50 p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span className="font-mono text-sm text-slate-400">{r.request_id}</span>
                  <RiskBadge value={r.risk} /><StatusBadge value={r.status} />
                </div>
                <h3 className="mt-2 font-display text-lg font-semibold text-slate-100">{r.requested_action} · {r.target_asset}</h3>
                <p className="mt-1 text-sm text-slate-400">{r.reason}</p>
                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
                  <Field k="Source" v={r.source} warn={r.source_type !== "AI Agent"} />
                  <Field k="Source Type" v={r.source_type} />
                  <Field k="Scope" v={r.scope} />
                  <Field k="Source IP" v={r.source_ip} mono />
                  <Field k="VPN/Proxy/Tor" v={r.vpn_proxy_tor} mono warn={["UNKNOWN","Tor exit node"].includes(r.vpn_proxy_tor)} />
                  <Field k="Requested" v={timeAgo(r.created_at)} />
                </dl>
              </div>
              <div className="grid shrink-0 grid-cols-2 gap-2 lg:w-72">
                {DECISIONS.map((d) => (
                  <button key={d.key} disabled={!!busy} onClick={() => decide(r, d.key)} data-testid={`decision-${d.key.toLowerCase()}-${r.id}`}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium disabled:opacity-50 ${d.cls}`}>
                    <d.icon className="h-3.5 w-3.5" /> {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {resolved.length > 0 && (
        <Panel title="Recent Decisions" className="mt-6">
          <div className="space-y-2">
            {resolved.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-800/70 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-200">{r.requested_action} · {r.target_asset}</p>
                  <p className="font-mono text-[11px] text-slate-500">{r.request_id} · {r.decided_by || "—"}</p>
                </div>
                <StatusBadge value={r.status} />
              </div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}

function Field({ k, v, mono, warn }) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{k}</dt>
      <dd className={`${mono ? "font-mono" : ""} ${warn ? "text-amber-400" : "text-slate-200"}`}>{v || "UNKNOWN"}</dd>
    </div>
  );
}
